import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TimelineItem, ItemCategory } from '@/types/item';
import { useUpdateItem, useDeleteItem } from '@/hooks/useItems';
import { useItemMilestones } from '@/hooks/milestones/useItemMilestones';
import { Trash2, Target, RotateCcw, User } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { calculateItemCompletion } from '@/utils/itemCompletion';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useItemsActionPermissions } from '@/hooks/useItemsPermissions';
import { useItemCategories } from '@/hooks/useItemCategories';
import { MilestoneManager } from './milestones/MilestoneManager';
// ✅ RETIRED (PR-F1): globalFeatureFlags import removed — ENABLE_SERVICE_ITEM_ASSIGNMENT hardcoded
import { useItemMilestonePermissions } from '@/hooks/milestones/useItemMilestonePermissions';
import { useProjectTeamMembers } from '@/hooks/useProjectTeamMembers';
import { evaluateRulesImperative } from '@/components/projects/control/rules/evaluateRulesImperative';
import RuleBlockModal from '@/components/projects/control/rules/RuleBlockModal';
import type { RuleActionContext, RuleResult } from '@/components/projects/control/rules/useRuleEngine';

interface ItemEditDialogProps {
  item: TimelineItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  availableItems?: TimelineItem[];
}

export default function ItemEditDialog({ item, isOpen, onClose, onSave, availableItems = [] }: ItemEditDialogProps) {
  const updateItem = useUpdateItem();
  const deleteItem = useDeleteItem();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // R6 Rule Engine state
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleResults, setRuleResults] = useState<RuleResult[]>([]);
  const [ruleContext, setRuleContext] = useState<RuleActionContext | null>(null);
  const { canEditBasic } = useItemsActionPermissions();
  const { data: itemCategories = [] } = useItemCategories();
  // ✅ HARDCODED (2025-11-09): Custom milestones always enabled (production-forced)
  const milestoneFeatureEnabled = true;
  const { canView: canViewMilestones } = useItemMilestonePermissions(item?.projectId);
  const { data: milestones = [] } = useItemMilestones(item?.id || '', { enabled: milestoneFeatureEnabled && !!item });
  
  // ✅ HARDCODED (PR-F1 2026-04-30): ENABLE_SERVICE_ITEM_ASSIGNMENT retired, isServiceProject guard preserved
  const isServiceProject = item?.projectStatus === 'service';
  const showAssigneeField = isServiceProject;
  
  // Fetch team members for assignee dropdown
  const { data: teamMembers = [] } = useProjectTeamMembers(item?.projectId || '', {
    enabled: showAssigneeField && !!item?.projectId
  });

  // === Milestones list for current item (used by Milestones tab / completion display) ===




  const [formData, setFormData] = useState({
    name: '',
    category: 'Sàn' as ItemCategory,
    categoryId: null as string | null,
    notes: '',
    startDate: '',
    endDate: '',
    manualCompletion: null as number | null,
    assignedUserId: null as string | null
  });
  const [useManualCompletion, setUseManualCompletion] = useState(false);

  // Load form draft or item data
  useEffect(() => {
    if (item) {
      const draftKey = `item-edit-draft-${item.id}`;
      try {
        const stored = localStorage.getItem(draftKey);
        if (stored) {
          const draft = JSON.parse(stored);
          const now = Date.now();
          const expiry = 24 * 60 * 60 * 1000; // 24 hours
          
          // Load draft if not expired
          if (draft.timestamp && (now - draft.timestamp) < expiry) {
            // Find category_id from category name if not in draft
            let categoryId = draft.categoryId ?? item.category_id;
            if (!categoryId && draft.category) {
              const matchedCategory = itemCategories.find(c => c.name === draft.category);
              categoryId = matchedCategory?.id || null;
            }
            
            setFormData({
              name: draft.name ?? item.name,
              category: draft.category ?? item.category,
              categoryId: categoryId,
              notes: draft.notes ?? (item.notes || ''),
              startDate: draft.startDate ?? (item.startDate || ''),
              endDate: draft.endDate ?? (item.endDate || ''),
              manualCompletion: draft.manualCompletion ?? item.manual_completion_percentage ?? null,
              assignedUserId: draft.assignedUserId ?? item.assigned_user_id ?? null
            });
            setUseManualCompletion(draft.useManualCompletion ?? (item.manual_completion_percentage !== null && item.manual_completion_percentage !== undefined));
            return;
          }
        }
      } catch (error) {
        console.warn('Failed to load item edit draft:', error);
      }
      
      // Load from item if no valid draft
      const hasManual = item.manual_completion_percentage !== null && item.manual_completion_percentage !== undefined;
      
      // Use category_id if available, otherwise find by category name
      let categoryId = item.category_id;
      if (!categoryId && item.category) {
        const matchedCategory = itemCategories.find(c => c.name === item.category);
        categoryId = matchedCategory?.id || null;
      }
      
      setFormData({
        name: item.name,
        category: item.category,
        categoryId: categoryId,
        notes: item.notes || '',
        startDate: item.startDate || '',
        endDate: item.endDate || '',
        manualCompletion: item.manual_completion_percentage ?? null,
        assignedUserId: item.assigned_user_id ?? null
      });
      setUseManualCompletion(hasManual);
    }
  }, [item, itemCategories]);

  // Base form values derived from the current item (no draft)
  const baseFormValues = useMemo(() => {
    if (!item) return null;

    const hasManual = item.manual_completion_percentage !== null && item.manual_completion_percentage !== undefined;

    // Use category_id if available, otherwise find by category name
    let categoryId = item.category_id;
    if (!categoryId && item.category) {
      const matchedCategory = itemCategories.find(c => c.name === item.category);
      categoryId = matchedCategory?.id || null;
    }

    return {
      name: item.name,
      category: item.category,
      categoryId,
      notes: item.notes || '',
      startDate: item.startDate || '',
      endDate: item.endDate || '',
      manualCompletion: item.manual_completion_percentage ?? null,
      assignedUserId: item.assigned_user_id ?? null,
      useManualCompletion: hasManual
    };
  }, [item, itemCategories]);

  // Persist form draft to localStorage only when form differs from item
  useEffect(() => {
    if (!item || !baseFormValues) return;

    const draftKey = `item-edit-draft-${item.id}`;
    try {
      const isUnchanged =
        formData.name === baseFormValues.name &&
        formData.category === baseFormValues.category &&
        formData.categoryId === baseFormValues.categoryId &&
        formData.notes === baseFormValues.notes &&
        formData.startDate === baseFormValues.startDate &&
        formData.endDate === baseFormValues.endDate &&
        formData.manualCompletion === baseFormValues.manualCompletion &&
        formData.assignedUserId === baseFormValues.assignedUserId &&
        useManualCompletion === baseFormValues.useManualCompletion;

      if (isUnchanged) {
        localStorage.removeItem(draftKey);
        return;
      }

      localStorage.setItem(draftKey, JSON.stringify({
        ...formData,
        useManualCompletion,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Failed to save item edit draft:', error);
    }
  }, [formData, useManualCompletion, item, baseFormValues]);

  const clearDraft = () => {
    if (!item) return;
    const draftKey = `item-edit-draft-${item.id}`;
    try {
      localStorage.removeItem(draftKey);
    } catch (error) {
      console.warn('Failed to clear item edit draft:', error);
    }
  };

  const executeSave = async () => {
    if (!item) return;
    
    try {
      const manualCompletion = useManualCompletion 
        ? Math.min(100, Math.max(0, Number(formData.manualCompletion ?? 0)))
        : null;
      
      await updateItem.mutateAsync({
        id: item.id,
        name: formData.name,
        category: formData.category,
        category_id: formData.categoryId,
        notes: formData.notes,
        start_date: formData.startDate,
        end_date: formData.endDate,
        manual_completion_percentage: manualCompletion,
        ...(showAssigneeField && { assigned_user_id: formData.assignedUserId })
      });



      
      clearDraft();
      if (onSave) await onSave();
      await new Promise(resolve => setTimeout(resolve, 300));
      onClose();
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  const handleSave = async () => {
    if (!item) return;
    
    // R6: Check if setting manual completion to 100% (and wasn't already 100%)
    const isSettingTo100 = useManualCompletion && formData.manualCompletion === 100;
    const wasAlready100 = item.manual_completion_percentage === 100;
    
    if (isSettingTo100 && !wasAlready100) {
      const context: RuleActionContext = {
        targetModule: 'plan',
        action: 'milestone_100',
        entityId: item.id,
        entityLabel: item.name,
      };
      const { results, canProceed } = await evaluateRulesImperative(item.projectId, context);
      
      if (!canProceed) {
        setRuleResults(results);
        setRuleContext(context);
        setShowRuleModal(true);
        return;
      }
    }

    await executeSave();
  };

  const handleDelete = async () => {
    if (!item) return;
    
    try {
      await deleteItem.mutateAsync(item.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  // Handle case when item no longer exists (e.g., deleted by another user)
  if (!item) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Hạng mục không tồn tại</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-muted-foreground">
            Hạng mục này có thể đã bị xóa hoặc không còn tồn tại.
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Item</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className={`grid w-full ${milestoneFeatureEnabled && canViewMilestones ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            {milestoneFeatureEnabled && canViewMilestones && (
              <TabsTrigger value="milestones">
                <Target className="h-4 w-4 mr-2" />
                Milestones
                {milestones.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{milestones.length}</Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="basic" className="space-y-4 mt-4">
            <div>
              <Label htmlFor="itemName">Item Name</Label>
              <Input
                id="itemName"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter item name"
                disabled={!canEditBasic}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  disabled={!canEditBasic}
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  disabled={!canEditBasic}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter notes"
                disabled={!canEditBasic}
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.categoryId || ''} 
                onValueChange={(categoryId: string) => {
                  const selectedCategory = itemCategories.find(c => c.id === categoryId);
                  if (selectedCategory) {
                    setFormData(prev => ({ 
                      ...prev, 
                      categoryId: selectedCategory.id,
                      category: selectedCategory.name as ItemCategory
                    }));
                  }
                }} 
                disabled={!canEditBasic}
              >
                <SelectTrigger>
                  <SelectValue>
                    {formData.categoryId 
                      ? itemCategories.find(c => c.id === formData.categoryId)?.name 
                      : 'Select category'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {itemCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assigned To - Service projects only */}
            {showAssigneeField && (
              <div className="space-y-2">
                <Label htmlFor="assignedTo" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Assigned To
                </Label>
                <Select
                  value={formData.assignedUserId || 'unassigned'}
                  onValueChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      assignedUserId: value === 'unassigned' ? null : value
                    }));
                  }}
                  disabled={!canEditBasic}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent className="z-50 bg-popover">
                    <SelectItem value="unassigned">
                      <span className="text-muted-foreground">Unassigned</span>
                    </SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        <span>{member.full_name || member.email}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-3 pt-4 border-t">
              <Label>Completion %</Label>
              <RadioGroup
                value={useManualCompletion ? 'manual' : 'auto'}
                onValueChange={(val) => setUseManualCompletion(val === 'manual')}
                disabled={!canEditBasic}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="auto" id="auto" />
                  <Label htmlFor="auto" className="font-normal cursor-pointer">
                    Auto-calculate
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({calculateItemCompletion(item, milestones as any, null)}% 
                      {milestones.length > 0 ? ' from milestones' : ' time-based'})
                    </span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="manual" id="manual" />
                  <Label htmlFor="manual" className="font-normal cursor-pointer">
                    Manual override
                  </Label>
                </div>
              </RadioGroup>
              
              {useManualCompletion && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.manualCompletion ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? null : Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                      setFormData(prev => ({ ...prev, manualCompletion: val }));
                    }}
                    placeholder="0-100"
                    disabled={!canEditBasic}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUseManualCompletion(false);
                      setFormData(prev => ({ ...prev, manualCompletion: null }));
                    }}
                    disabled={!canEditBasic}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {milestoneFeatureEnabled && canViewMilestones && (
            <TabsContent value="milestones" className="mt-4">
              <MilestoneManager
                itemId={item.id}
                projectId={item.projectId}
                projectStatus={item.projectStatus}
                itemStartDate={formData.startDate}
                itemEndDate={formData.endDate}
              />
            </TabsContent>
          )}

        </Tabs>



        <DialogFooter>
          {canEditBasic && (
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(true)}
              className="mr-auto"
              disabled={deleteItem.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => {
            clearDraft();
            onClose();
          }}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canEditBasic || updateItem.isPending}>
            {updateItem.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Xác nhận xóa Item"
        description={`Bạn có chắc chắn muốn xóa item "${item?.name}"? Hành động này không thể hoàn tác.`}
        confirmText="Xóa"
        cancelText="Hủy"
        variant="destructive"
        isLoading={deleteItem.isPending}
      />
    </Dialog>

    {/* R6 Rule Block Modal */}
    {ruleContext && (
      <RuleBlockModal
        isOpen={showRuleModal}
        onClose={() => setShowRuleModal(false)}
        onOverride={() => {
          executeSave();
        }}
        results={ruleResults}
        actionContext={ruleContext}
        projectId={item?.projectId || ''}
      />
    )}
    </>
  );
}